import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { has, getOr } from 'lodash/fp';
import { useForm } from 'react-hook-form';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

export const MAX_REASON_LENGTH = 1000;

const validationSchema = yup.object().shape({
  cancellationReason: yup
    .string()
    .trim()
    .required('Please tell us why you are canceling')
    .max(
      MAX_REASON_LENGTH,
      `Cancellation reason must not exceed ${MAX_REASON_LENGTH} characters`
    )
});

export default function CancelReservationDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  disabled,
  ...props
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { register, handleSubmit, errors, reset } = useForm({
    validationSchema,
    defaultValues: { cancellationReason: '' }
  });

  useEffect(() => {
    if (open) reset({ cancellationReason: '' });
  }, [open]);

  const isError = field => has(field, errors);
  const getFieldError = field => getOr('', `${field}.message`, errors);

  const onSubmit = ({ cancellationReason }) => {
    onConfirm(cancellationReason.trim());
  };

  return (
    <Dialog
      {...props}
      open={open}
      fullScreen={fullScreen}
      onClose={onClose}
      aria-labelledby="cancel-reservation-dialog-title"
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle id="cancel-reservation-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
          <TextField
            autoFocus
            required
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            size="small"
            name="cancellationReason"
            label="Reason for cancellation"
            disabled={disabled}
            inputRef={register}
            error={isError('cancellationReason')}
            helperText={
              getFieldError('cancellationReason') ||
              'Your reason is shared with the pantry.'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button type="submit" color="primary" disabled={disabled}>
            Yes, cancel reservation
          </Button>
          <Button onClick={onClose} color="primary" disabled={disabled}>
            No
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

CancelReservationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  disabled: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
};

CancelReservationDialog.defaultProps = {
  title: 'Confirm Cancel',
  message:
    'Are you sure you want to cancel this reservation? Please tell us why.',
  disabled: false
};
